import { useEffect, useState } from 'react';
import type { Modifier, PopperChildrenProps, StrictModifier } from 'react-popper';
import { usePopper } from 'react-popper';

import { Popover, PopoverPanel, Transition } from '@headlessui/react';
import clsx from 'clsx';

type UsePopperReturnType = ReturnType<typeof usePopper>;

export type PopoverChildrenProps = {
    open: boolean;
    update: UsePopperReturnType['update'];
    referenceElement?: HTMLButtonElement;
    setReferenceElement: (element: HTMLButtonElement) => void;
    setPopperElement: (element: HTMLElement) => void;
    styles: UsePopperReturnType['styles'];
    attributes: UsePopperReturnType['attributes'];
};

export type PopoverWrapperProps = {
    children: (args: PopoverChildrenProps) => React.ReactElement;
    popperOptions?: {
        placement?: PopperChildrenProps['placement'];
        offset?: Modifier<'offset'>['options']['offset'];
        modifiers?: ReadonlyArray<Modifier<StrictModifier['name']>>;
    };
};

export function PopoverWrapper({ children, popperOptions }: PopoverWrapperProps) {
    const [referenceElement, setReferenceElement] = useState<HTMLButtonElement>();

    const [popperElement, setPopperElement] = useState<HTMLElement>();

    const modifiers = popperOptions?.modifiers ?? [];

    const { styles, attributes, update } = usePopper(referenceElement, popperElement, {
        placement: popperOptions?.placement ?? 'bottom-start',
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: popperOptions?.offset ?? [0, 7]
                }
            },
            ...modifiers
        ]
    });

    return (
        <Popover className="relative">
            {({ open }) => (
                <UpdatePositionOnMount open={open} update={update}>
                    {children({
                        open,
                        update,
                        referenceElement,
                        setReferenceElement,
                        setPopperElement,
                        styles,
                        attributes
                    })}
                </UpdatePositionOnMount>
            )}
        </Popover>
    );
}

type PopoverContainerProps = Omit<PopoverChildrenProps, 'setReferenceElement'> & {
    className?: string;
    width?: string;
    children: React.ReactNode;
};

export function UpdatePositionOnMount({
    open,
    update,
    children
}: {
    update: UsePopperReturnType['update'];
    open: boolean;
    children: React.ReactNode;
}) {
    useEffect(() => {
        if (open) {
            setTimeout(() => update(), 10);
        }
    }, [update, open]);

    return children;
}

export function PopoverContainer({
    open,
    children,
    className = 'z-10 relative rounded-xl bg-white shadow-icoM dark:border dark:border-icoDarkMode-beton dark:bg-icoDarkMode-deepBlack overflow-hidden',
    width = 'w-72',
    setPopperElement,
    styles,
    attributes
}: Omit<PopoverContainerProps, 'update'>) {
    return (
        <PopoverPanel static ref={setPopperElement} style={styles.popper} {...attributes.popper}>
            <Transition
                show={open}
                enter="ease-out duration-200"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1">
                <div className={clsx(className, width)}>
                    <div className="relative">{children}</div>
                </div>
            </Transition>
        </PopoverPanel>
    );
}
